import {Root} from "./TileRemoveButton.styles";

export type TileRemoveButtonProps = {
	/**
	 * The control's accessible name, from the caller's catalog. Required rather than defaulted: the two
	 * surfaces name the same gesture differently and both namings are right — the account editor takes
	 * an image out of a saved THEME, the composer removes it from THIS transfer.
	 */
	label: string;
	/** Optional pointer tooltip. The glyph alone says "remove"; this says remove from WHAT. */
	title?: string;
	disabled?: boolean;
	onClick: () => void;
	/**
	 * Composition seam for styled-components (ui#1942).
	 *
	 * The builder's logo tile takes this control and bleeds its hit area out to 44px, which the picture
	 * tiles cannot do — they sit in a wrapped row where a larger target would reach into the neighbour.
	 * Forwarding the class is what lets that be `styled(TileRemoveButton)` rather than a second ✕ button,
	 * which is exactly the drift this shared component exists to prevent.
	 */
	className?: string;
};

/**
 * The red ✕ overlaid on a selected image's top-right corner (ui#760).
 *
 * ONE component for both picture pickers — the account personalization theme editor
 * ({@link ./PageThemeSection}) and the per-transfer composer ({@link home/components/PersonaliseSheet}).
 * They already share the tile grammar in `./PageThemeSection.styles`; sharing the control itself is what
 * keeps the two from drifting into two different-looking ✕ buttons, which is exactly what happened to
 * the affordance this replaces.
 *
 * It only ever REMOVES FROM THE SELECTION — ui#686's semantics, unchanged: the picture stays in the
 * sender's library and its bytes are untouched. The permanently-destructive control is a different
 * button in a different place with a different glyph ({@link ./TileDeleteButton}, a bin in the control
 * row of an unselected tile — ui#766), and this one must never be wired to it.
 *
 * Dumb by mandate (epic #26): it holds no state and decides nothing, not even whether removal is
 * allowed — the caller's hook owns that and passes `disabled`.
 */
export function TileRemoveButton({label, title, disabled, onClick, className}: TileRemoveButtonProps) {
	return (
		<Root aria-label={label} title={title} disabled={disabled} onClick={onClick} className={className}>
			{/*
			 * The glyph is decorative — `aria-label` above is what a screen reader announces, and it names
			 * which picture this removes. A literal here is not user-visible copy for the same reason the
			 * tile's other glyph controls (+, ‹, ›) are not.
			 */}
			<span aria-hidden="true">✕</span>
		</Root>
	);
}
